import { CircleCheck, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { GlassCard } from '@/components/ui/glass-card';
import { cn } from '@/lib/utils';

/** A section of the operator screen: title, one-line purpose, its form. */
export function Section({
  title,
  intro,
  children,
  className,
}: {
  title: string;
  intro?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <GlassCard className={cn('rounded-3xl p-6', className)}>
      <h3 className="text-[1.0625rem] font-semibold tracking-tight text-call-ink">{title}</h3>
      {intro && <p className="mt-1 text-sm leading-relaxed text-call-dim">{intro}</p>}
      <div className="mt-5">{children}</div>
    </GlassCard>
  );
}

/** Labelled control on the dark surface. */
export function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[0.8125rem] font-medium text-call-ink">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-call-dim">{hint}</p>}
    </div>
  );
}

/**
 * Outcome of the last action in a section. An error is an alert; a
 * confirmation is a status. Both stay put until the next action replaces
 * them, so nothing lands in silence and nothing vanishes before it is read.
 */
export function Notice({ tone, children }: { tone: 'error' | 'ok'; children: ReactNode }) {
  const error = tone === 'error';
  return (
    <p
      role={error ? 'alert' : 'status'}
      className={cn(
        'mt-4 flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm',
        error ? 'bg-hangup/15 text-[#ffb3ad]' : 'bg-green/15 text-[#9fe4b0]',
      )}
    >
      {error ? (
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      ) : (
        <CircleCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      )}
      <span>{children}</span>
    </p>
  );
}

/** Radio row in the pre-join style: the whole row is the target. */
export function RadioRow({
  name,
  value,
  checked,
  onChange,
  label,
  detail,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  detail?: string;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors',
        checked ? 'border-blue/70 bg-blue/10' : 'border-white/10 hover:bg-white/5',
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="mt-1 size-4 accent-[#0071e3]"
      />
      <span>
        <span className="block text-sm font-medium text-call-ink">{label}</span>
        {detail && <span className="block text-xs text-call-dim">{detail}</span>}
      </span>
    </label>
  );
}
